<template>
  <v-dialog
    :model-value="modelValue"
    max-width="860"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="applicant-log-dialog__title">
        {{ title }}
      </v-card-title>
      <v-card-text>
        <v-alert
          v-if="errors.length"
          type="error"
          variant="tonal"
          class="applicant-log-dialog__alert"
        >
          <div v-for="(error, index) in errors" :key="index">
            {{ error }}
          </div>
        </v-alert>

        <div v-if="loading" class="applicant-log-dialog__loading">
          <v-skeleton-loader type="list-item-two-line" />
          <v-skeleton-loader type="list-item-two-line" />
        </div>

        <div v-else-if="!items.length && !errors.length" class="applicant-log-dialog__empty">
          Событий пока нет.
        </div>

        <div v-else-if="items.length" class="applicant-log-list">
          <div v-for="event in items" :key="event.id" class="applicant-log-event">
            <div class="applicant-log-event__meta">
              <span>{{ event.date }}</span>
              <span>{{ event.actor }}</span>
            </div>
            <div class="applicant-log-event__description">
              {{ event.description || 'Событие анкеты' }}
            </div>

            <div v-if="event.changes && event.changes.length" class="applicant-log-event__changes">
              <div
                v-for="change in event.changes"
                :key="change.field"
                class="applicant-log-change"
              >
                <div class="applicant-log-change__label">
                  {{ change.label || change.field }}
                </div>
                <div class="applicant-log-change__values">
                  <div>
                    <span>Было:</span>
                    {{ formatValue(change.old) }}
                  </div>
                  <div>
                    <span>Стало:</span>
                    {{ formatValue(change.new) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" variant="flat" @click="close">
          Закрыть
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ApplicantLogDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    applicationId: {
      type: [Number, String],
      default: null
    },
    title: {
      type: String,
      default: 'Лог заявления'
    }
  },
  emits: ['update:modelValue', 'update:loading'],
  data() {
    return {
      loading: false,
      errors: [],
      items: [],
      abortController: null
    }
  },
  computed: {
    logRequestKey() {
      return this.modelValue && this.applicationId
        ? String(this.applicationId)
        : ''
    }
  },
  watch: {
    logRequestKey: {
      immediate: true,
      handler(key) {
        if (key) {
          this.loadLog()
        } else {
          this.abortRequest()
        }
      }
    }
  },
  beforeUnmount() {
    this.abortRequest()
  },
  methods: {
    close() {
      this.$emit('update:modelValue', false)
    },
    setLoading(loading) {
      this.loading = loading
      this.$emit('update:loading', loading)
    },
    abortRequest() {
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }

      if (this.loading) {
        this.setLoading(false)
      }
    },
    loadLog() {
      if (!this.$model.isAdmin || !this.applicationId) {
        return
      }

      this.abortRequest()
      const abortController = new AbortController()
      this.abortController = abortController
      this.errors = []
      this.items = []
      this.setLoading(true)

      const requestData = new FormData()
      requestData.append('id', this.applicationId)

      this.axios
        .post(
          this.$model.wwwroot +
          '/procollege/manage_groups/admission/createApplicant/getApplicantLog',
          requestData,
          { signal: abortController.signal }
        )
        .then((response) => {
          if (this.abortController !== abortController) {
            return
          }

          const data = response.data || {}
          const errors = Array.isArray(data.errors) ? data.errors : []

          this.errors = errors
          this.items = errors.length
            ? []
            : (Array.isArray(data.items) ? data.items : [])
        })
        .catch((error) => {
          if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
            return
          }

          const data = error.response && error.response.data
            ? error.response.data
            : {}

          this.errors = Array.isArray(data.errors) && data.errors.length
            ? data.errors
            : ['Не удалось загрузить лог анкеты']
          this.items = []
        })
        .finally(() => {
          if (this.abortController !== abortController) {
            return
          }

          this.abortController = null
          this.setLoading(false)
        })
    },
    formatValue(value) {
      if (value === null || value === undefined || value === '') {
        return 'не заполнено'
      }

      return String(value)
    }
  }
}
</script>

<style scoped>
.applicant-log-dialog__title {
  white-space: normal;
  overflow-wrap: anywhere;
}

.applicant-log-dialog__alert,
.applicant-log-dialog__loading {
  margin-bottom: 16px;
}

.applicant-log-dialog__empty {
  padding: 12px 0;
  color: #667085;
}

.applicant-log-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.applicant-log-event {
  padding: 14px;
  background: #f8fafc;
  border: 1px solid #e6e8ee;
  border-radius: 8px;
}

.applicant-log-event__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 6px;
  color: #667085;
  font-size: 13px;
}

.applicant-log-event__description {
  color: #1f2937;
  font-weight: 700;
}

.applicant-log-event__changes {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.applicant-log-change {
  padding: 10px;
  background: #ffffff;
  border: 1px solid #e6e8ee;
  border-radius: 6px;
}

.applicant-log-change__label {
  margin-bottom: 6px;
  color: #344054;
  font-weight: 700;
}

.applicant-log-change__values {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  color: #475467;
  font-size: 13px;
}

.applicant-log-change__values span {
  color: #667085;
  font-weight: 700;
}

@media (max-width: 600px) {
  .applicant-log-change__values {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
